/*
OPTIONS
pkce: Use PKCE Authorization grant (preferred)
authoriseUri: The sso server's authorise url which will have the clientId, callback, nonce params appended
tokenUri: The sso server's get token POST url for fetching tokens after authorise code is returned in PKCE mode
logoutUri: The sso server's logout uri where the user will be sent on log out
clientSecret: The client_secret to post when fetching auth code with pkce for authorization code grant configs

params: URL parameters passed when redirecting to OAuth provider
params.client_id: The application client id provided by the sso server
params.redirect_uri: The url to redirect back to after sso login is complete. This is normally <baseUrl>/login/callback

router: The vue router instance. When passed, the login/callback route and route guards are automatically bootstrapped
apiHttpHeaders: parameters to pass when building api requests with buildHttpHeader. Authorize, id-token and X-Correlation-ID are passed by default

secureApp: All routes are protected by default
enablePopupAuth: Enables popup auth which attempts to refresh tokens via popup without losing user state, default (false).

afterLogin: callback function after login flow completed. Receives access and id tokens as parameters on success, or undefined on failure.

devIdTokenOverride: use different id token in dev mode
forceProdMode: get auth from OAuth server in development mode
*/

import authCrypto from './authCrypto';
import helpers from './helpers';
import routeHelper from './routeHelper';

const OAuth = {
  install: function (Vue, options) {
    if (!options.authoriseUri) {
      throw "OAuth plugin error. You must pass 'authoriseUri' in options";
    }
    if (!options.params.client_id) {
      throw "OAuth plugin error. You must pass 'params.client_id' in options";
    }
    if (!options.params.redirect_uri) {
      throw "OAuth plugin error. You must pass 'params.redirect_uri' in options";
    }

    if (options.router) {
      routeHelper.attachOAuthRoutes(options.router, options.params.redirect_uri);
    }

    Vue.prototype.$OAuth = {
      authoriseUri: options.authoriseUri,
      tokenUri: options.tokenUri,
      logoutUri: options.logoutUri,
      postLogoutUri: options.postLogoutUri,
      apiHttpHeaders: options.apiHttpHeaders || {},
      secureApp: options.secureApp || false,
      enablePopupAuth: options.enablePopupAuth || false,
      pkce: options.pkce,
      clientSecret: options.clientSecret,

      afterLogin: options.afterLogin,

      devIdTokenOverride: options.devIdTokenOverride,
      forceProdMode: options.forceProdMode,

      params: Object.assign({
        // you need to pass client_id, redirect_uri
        response_type: options.pkce ? 'code' : encodeURIComponent('token id_token')
      }, options.params),

      // state props
      data: Vue.observable({
        idToken: null,
        accessToken: null,
        tokenType: null,
        profile: null,
        loaded: false
      }),

      // expose observable props as getters
      get loaded() {
        return this.data.loaded
      },
      get profile() {
        return this.data.profile
      },

      // functions
      clearToken: function () {
        this.data.idToken = null;
        this.data.accessToken = null;
        this.data.tokenType = null;
        // leave profile to avoid name disappearing from profile menu

        this.data.loaded = false;

        localStorage.removeItem('id_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_type');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('refresh_token');

        sessionStorage.removeItem('nonce');
        sessionStorage.removeItem('codeVerifier');
        sessionStorage.removeItem('oauth-session-initialised');
      },
      isAuthenticated: function () {
        let tokenExpired = true;

        if (localStorage.getItem('access_token')) {

          this.setToken(localStorage.getItem('access_token'), localStorage.getItem('id_token'), localStorage.getItem('token_type'), localStorage.getItem('refresh_token'));
          // check expiry
          const expiryEpoch = localStorage.getItem('token_expiry');
          if (expiryEpoch) {
            const expDate = new Date(parseFloat(expiryEpoch * 1000));
            tokenExpired = new Date() >= expDate;
          }

          const localTokenValid = !tokenExpired &&
            sessionStorage.getItem('oauth-session-initialised') === 'true' &&
            this.data.accessToken != null;


          return localTokenValid;
        }

        return false;
      },
      getAuth: async function () {

        if (process.env.NODE_ENV === 'development' && !this.forceProdMode) {
          //bypass log in for dev
          const accessToken = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJPbmxpbmUgSldUIEJ1aWxkZXIiLCJpYXQiOjE1NTk1MjI2NDIsImV4cCI6NDA4NDA0NDI0MiwiYXVkIjoid3d3LmV4YW1wbGUuY29tIiwic3ViIjoianJvY2tldEBleGFtcGxlLmNvbSIsImdpdmVuTmFtZSI6IlRlc3QiLCJzbiI6IlVzZXIiLCJFbWFpbCI6InRlc3QudXNlckBkZXQubnN3LmVkdS5hdSIsIlJvbGUiOlsiTWFuYWdlciIsIlByb2plY3QgQWRtaW5pc3RyYXRvciJdfQ.kNoYM0Bgf9tVv5iqbeLAJ2uus8WTskcoNE7YH5siCCA';
          const idToken = this.devIdTokenOverride || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJPbmxpbmUgSldUIEJ1aWxkZXIiLCJpYXQiOjE1NTk1MjI2NDIsImV4cCI6NDA4NDA0NDI0MiwiYXVkIjoid3d3LmV4YW1wbGUuY29tIiwic3ViIjoianJvY2tldEBleGFtcGxlLmNvbSIsImdpdmVuTmFtZSI6IlRlc3QiLCJzbiI6IlVzZXIiLCJlbWFpbCI6InRlc3QudXNlckBkZXQubnN3LmVkdS5hdSIsInJvbGVzIjpbIlNDSE9PTC5QUklOQ0lQQUwiXX0.rRYKWPY-HfxOT6HzNqhqauKmD221wfEYeaAs2PEkTWk';

          this.setToken(accessToken, idToken, 'Bearer');

          return true;
        }

        const localStoreAccessToken = localStorage.getItem('access_token');
        const localStoreIdToken = localStorage.getItem('id_token');
        const localStoreRefreshToken = localStorage.getItem('refresh_token');

        if (!localStoreAccessToken || !this.isAuthenticated()) {
          // go get a new token
          this.clearToken();

          const nonce = authCrypto.randomString(16);
          sessionStorage.setItem('nonce', nonce); // store to validate after redirect
          const paramsMap = {
            nonce: await authCrypto.generateHashArray(nonce),
            ...this.params
          };

          if (this.pkce) {
            const codeVerifier = authCrypto.randomString(128);
            sessionStorage.setItem('codeVerifier', codeVerifier); // store to validate after redirect

            const codeChallenge = await authCrypto.generateChallenge(codeVerifier); //SHA256 -> Base64URL
            const codeChallengeMethod = 'S256';
            Object.assign(paramsMap, {
              code_challenge: codeChallenge,
              code_challenge_method: codeChallengeMethod
            });
          }

          const urlParams = Object.entries(paramsMap).map(e => helpers.encodeUriComponent(e).join('=')).join('&');
          const authUrl = this.authoriseUri + '?' + urlParams;

          const isIE = document.documentMode != null;
          if (!localStoreAccessToken || isIE || !this.enablePopupAuth) {
            window.location = authUrl;
          } else {
            // token expired
            try {
              // popup auth to try and save app state
              await this.refreshToken(authUrl)
                .then((token) => {
                  this.setToken(token.accessToken, token.idToken, token.tokenType, token.refreshToken);
                  return true;
                });
            } catch (err) {
              // popup auth failed. Send them directly to the login page.
              window.location = authUrl;
            }
          }
        } else if (localStoreAccessToken && !this.data.accessToken) {
          // reset token into local storage in case of refresh
          this.setToken(localStoreAccessToken, localStoreIdToken, localStorage.getItem('token_type'), localStoreRefreshToken);
          return true;
        }

        // return false when auth redriect was required
        return false;
      },
      setToken(accessToken, idToken, tokenType, refreshToken) {
        if (accessToken && accessToken !== 'undefined' && idToken && idToken !== 'undefined') {
          const tData = helpers.parseJwt(idToken);

          this.data.accessToken = accessToken;
          this.data.tokenType = tokenType;
          this.data.idToken = idToken;
          this.data.profile = tData;

          localStorage.setItem('id_token', idToken);
          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('token_type', tokenType);
          localStorage.setItem('token_expiry', tData.exp);

          if (refreshToken) {
            // refresh token only supplied with PKCE auth
            localStorage.setItem('refresh_token', refreshToken);
          }

          sessionStorage.setItem('oauth-session-initialised', 'true');

          this.data.loaded = true;
          if (typeof this.afterLogin === 'function') {
            this.afterLogin(accessToken, idToken);
          }
        } else {
          this.clearToken();
        }
      },
      getLogoutUri() {
        let logout = this.logoutUri;
        const idToken = localStorage.getItem('id_token')
        if (this.postLogoutUri && idToken) {
          logout = `${this.logoutUri}?id_token_hint=${idToken}&post_logout_redirect_uri=${this.postLogoutUri}`
        }
        return logout
      },
      refreshToken(url) {
        // pop up a window and wait for token to be set by login/callback route in popup

        const tokenPromise = new Promise((resolve) => {
          var w = window.innerWidth;
          var h = window.innerHeight;
          var y = window.top.outerHeight / 2 + window.top.screenY - (h / 2);
          var x = window.top.outerWidth / 2 + window.top.screenX - (w / 2);

          const popupWindow = window.open(url, 'authPopup', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=' + w + ', height=' + h + ', top=' + y + ', left=' + x);

          if (!popupWindow || popupWindow.closed || typeof popupWindow.closed == 'undefined') {
            // popup was blocked
            throw 'Authentication pop up was blocked';
          }

          // poll for a token to be set or popup to be closed
          const pollTimer = window.setInterval(() => {
            try {
              const localStoreIdToken = localStorage.getItem('id_token');
              const localStoreAccessToken = localStorage.getItem('access_token');
              const localStoreTokenType = localStorage.getItem('token_type');
              const localStoreRefreshToken = localStorage.getItem('refresh_token');
              if (localStoreAccessToken) {
                window.clearInterval(pollTimer);

                // wait a bit for the page to load because closing immediately caused the icons on the main page to not load correctly.
                setTimeout(() => {
                  popupWindow.close()
                }, 250);
                resolve({accessToken: localStoreAccessToken, idToken: localStoreIdToken, tokenType: localStoreTokenType, refreshToken: localStoreRefreshToken});
              }
              if (popupWindow.closed) {
                throw 'Could not authenticate.';
              }
            } catch (e) {
              // wait...
              console.info('Waiting for authorisation token...'); // eslint-disable-line no-console
            }
          }, 250);
        });
        return tokenPromise;
      },
      buildHttpHeader(header) {
        const authHeaders = (this.data.tokenType && this.data.accessToken && this.data.idToken) ? {
          ...this.apiHttpHeaders,
          'Authorization': this.data.tokenType + ' ' + this.data.accessToken,
          'id-token': this.data.idToken
        } : {};
        return {...authHeaders, ...(header || {})};
      },
      logout() {
        const logoutUri = this.getLogoutUri()
        this.clearToken();
        window.location = logoutUri
      }
    }
  }
}
export default OAuth;
